/**
 * FoliarBoardPage — the foliar workspace's landing surface (F1).
 *
 * One row per foliar round with real progress. The round is the unit a manager
 * plans, converts, assigns and signs off, and until F1 it had no page at all.
 *
 * Row click targets /nutrition/foliar/programmes/:programmeId/rounds/:roundId,
 * which F2 builds. The path carries the programme because roundId is unique
 * within a programme only.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCard, DataTable, KpiTile, Notice, StatusPill } from '../patterns';
import type { StatusVariant } from '../patterns';
import { nutritionService, FoliarBoard, FoliarBoardRound } from '../../services/nutritionService';
import { nutritionSessionService } from '../../services/nutritionSessionService';
import { formatMonthName } from '../../dateFormat';
import { orderRounds, roundState, summariseFoliarBoard } from './foliarBoardSummary';
import type { RoundState } from './foliarBoardSummary';
import { FoliarWorkspaceContext } from './FoliarWorkspaceLayout';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import './nutrition.css';

// `roundState` is imported, not redefined. A local copy here would risk
// disagreeing with foliarBoardSummary's classifier on the completed-but-unsigned
// case, putting an "In progress" pill above a delta reading "0 in progress,
// 1 planned". One classifier, one truth.
// `StatusVariant` is exactly 'ok' | 'warn' | 'danger' | 'info' | 'queued'
// (StatusPill.tsx:4) — there is no 'mut'. 'queued' is the muted/neutral tone.
const STATE_VARIANT: Record<RoundState, StatusVariant> = {
  notCreated: 'queued', planned: 'info', inProgress: 'warn', complete: 'ok',
};

const FoliarBoardPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { programme } = useOutletContext<FoliarWorkspaceContext>();
  const [board, setBoard] = useState<FoliarBoard | null>(null);
  const [error, setError] = useState('');
  const [skippedNotice, setSkippedNotice] = useState('');
  const [busyRound, setBusyRound] = useState('');
  // Tracks whether the CURRENT `error` was set by a failed create, not by
  // load() itself — load() runs in onCreate's `finally` to refresh the board,
  // and its success path must not silently erase a create failure the user
  // hasn't seen yet (branch review finding 1).
  const createErrorRef = useRef(false);

  const load = useCallback(async () => {
    if (!programme) return;
    try {
      setBoard(await nutritionService.getFoliarBoard(programme._id));
      if (!createErrorRef.current) setError('');
    } catch (_err) {
      setError(t('nutrition.foliar.board.loadFailed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programme?._id]);

  useEffect(() => { load(); }, [load]);

  // The layout's own trail does not re-run its effect on a tab round trip: the
  // sibling tab clears the crumbs on unmount and the layout's key is unchanged,
  // so the top bar goes empty. Registering the same trail here — a hook, so it
  // must sit above every early return — keeps the Board tab's breadcrumb
  // populated. The provider dedupes identical trails, so this causes no churn.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    { label: t('nutrition.foliar.workspace.title') },
  ]);

  const onCreate = async (r: FoliarBoardRound) => {
    if (!programme) return;
    // Clear the shared error slot BEFORE this flow starts — otherwise a stale
    // notice from a previous failure could still be attributed to this round,
    // or (worse) this round's own failure could be erased by load()'s success
    // path below without the ref guard.
    setError('');
    setSkippedNotice('');
    createErrorRef.current = false;
    setBusyRound(r.roundId);
    try {
      // Reuse the existing, already-unit-tested converter — do not add a
      // second wrapper for this write path. An empty orchardNos[] means
      // EVERY orchard in the round's groups, not "none".
      const result = await nutritionSessionService.convertRound(programme._id, r.roundId, []);
      if (result.skipped.length > 0) {
        setSkippedNotice(t('nutrition.foliar.board.createSkipped', { count: result.skipped.length }));
      }
    } catch (err: any) {
      createErrorRef.current = true;
      /*
       * #475 — conversion refuses a cross-family dose unit, and this button is
       * where conversion is TRIGGERED, so this is where the manager meets that
       * refusal. The generic message names no cause, no product and no remedy
       * for a button that worked yesterday.
       *
       * Branch on the stable `error` identifier, never on the server's prose
       * (P2a2/D4): the message is a raw sentence that can be reworded at any
       * time. The copy is the same string the round page renders for rounds
       * already converted, because the two answers come from one detector and
       * must not drift.
       */
      const data = err?.response?.data;
      if (err?.response?.status === 400 && data?.error === 'unit-conflict') {
        const products = (data.conflicts || [])
          .map((c: { productName?: string }) => c.productName)
          .filter(Boolean)
          .join(', ');
        setError(t('nutrition.foliar.round.unitConflict', { products }));
      } else {
        setError(t('nutrition.foliar.board.createFailed'));
      }
    } finally {
      setBusyRound('');
      await load();
    }
  };

  const openRound = (r: Pick<FoliarBoardRound, 'roundId'>) => {
    if (!programme) return;
    navigate(`/nutrition/foliar/programmes/${programme._id}/rounds/${r.roundId}`);
  };

  /*
   * ONE round-link renderer for BOTH the table column and the mobile card.
   *
   * The card used to render the round name as inert <strong> text while only
   * the table column carried the button, so below 768px — where the CSS swaps
   * table for cards — there was NO way into a round at all. Users had to turn
   * the phone sideways. Same defect class as the programme actions menu in
   * #457: a control that exists in one view and not the other.
   *
   * jsdom applies no CSS, so BOTH views render in tests and a shared testid
   * would make getByTestId ambiguous — hence the distinct prefix per call site.
   * Keep them sharing this function; do not fork it.
   */
  const renderRoundLink = (r: Pick<FoliarBoardRound, 'roundId' | 'name'>, testIdPrefix: string) => (
    <button
      type="button"
      className="ntr-fbd-roundlink"
      data-testid={`${testIdPrefix}-${r.roundId}`}
      onClick={() => openRound(r)}
    >
      {r.name}
    </button>
  );

  // The error branch returns BEFORE the loading guard, so a failed load renders
  // a notice rather than sitting on "loading" forever.
  if (error && !board) {
    return <div data-testid="board-error"><Notice tone="danger">{error}</Notice></div>;
  }
  if (!programme) return null;                    // layout renders the empty state
  if (!board) return <div>{t('common.loading')}</div>;

  const rounds = orderRounds(board.rounds);
  // Summarise the ORDERED rounds. Passing `board` raw would mean the
  // attention strip's `.slice(0, 3)` truncated by the programme's array order
  // while the table below rendered board order — two different "first three".
  const { kpi, attention } = summariseFoliarBoard({ ...board, rounds });

  const progressOf = (r: FoliarBoardRound) => {
    const s = r.sessions;
    if (s.total === 0) return null;
    const done = s.signedOff;
    if (s.awaitingSignoff > 0) {
      return t('nutrition.foliar.board.progress.awaiting', {
        applied: s.completed + s.signedOff, awaiting: s.awaitingSignoff,
      });
    }
    if (s.draft > 0) {
      return t('nutrition.foliar.board.progress.drafts', {
        done, total: s.total, drafts: s.draft,
      });
    }
    return t('nutrition.foliar.board.progress.signedOff', { done, total: s.total });
  };

  return (
    <div className="ntr-ws-body" data-testid="foliar-board">
      {error && <Notice tone="danger" testId="board-error-notice">{error}</Notice>}
      {skippedNotice && <Notice tone="warn" testId="board-skipped-notice">{skippedNotice}</Notice>}

      <div className="pat-kpi-row">
        <KpiTile primary
          label={t('nutrition.foliar.board.kpi.inFlight')}
          value={kpi.roundsInFlight}
          delta={<span data-testid="foliar-kpi-inflight-delta">
            {t('nutrition.foliar.board.kpi.inFlightDelta', {
              inProgress: kpi.roundsInProgress, planned: kpi.roundsPlanned,
            })}
          </span>} />
        <KpiTile alert={kpi.awaitingSignoff > 0}
          label={t('nutrition.foliar.board.kpi.awaiting')}
          value={<span data-testid="foliar-kpi-awaiting">{kpi.awaitingSignoff}</span>}
          delta={kpi.oldestAwaitingDays === null
            ? undefined
            : t('nutrition.foliar.board.kpi.awaitingDelta', { count: kpi.oldestAwaitingDays })} />
        <KpiTile
          label={t('nutrition.foliar.board.kpi.applied')}
          value={<span data-testid="foliar-kpi-applied">{kpi.appliedThisMonth}</span>} />
        <KpiTile
          label={t('nutrition.foliar.board.kpi.drafts')}
          value={<span data-testid="foliar-kpi-drafts">{kpi.draftSessions}</span>}
          // Guarded — unlike awaitingDelta this would otherwise render
          // unconditionally, so a clean board would read "across 0 rounds".
          delta={kpi.roundsWithDrafts === 0
            ? undefined
            : t('nutrition.foliar.board.kpi.draftsDelta', { count: kpi.roundsWithDrafts })} />
      </div>

      <div className="ntr-fbd-attention">
        {attention.awaitingSignoff.length > 0 && (
          <div data-testid="board-attention-awaiting">
            <AlertCard tone="danger"
              count={attention.awaitingSignoff.reduce((s, a) => s + a.count, 0)}
              title={t('nutrition.foliar.board.attention.awaiting')}>
              <ul className="ntr-fbd-attention__list">
                {attention.awaitingSignoff.slice(0, 3).map((a) => (
                  <li key={a.roundId}>{a.name} — {a.count}</li>
                ))}
              </ul>
            </AlertCard>
          </div>
        )}
        {attention.drafts.length > 0 && (
          <div data-testid="board-attention-drafts">
            <AlertCard tone="warn" count={attention.drafts.reduce((s, a) => s + a.count, 0)}
              title={t('nutrition.foliar.board.attention.drafts')}>
              <ul className="ntr-fbd-attention__list">
                {attention.drafts.slice(0, 3).map((a) => (
                  <li key={a.roundId}>{a.name} — {a.count}</li>
                ))}
              </ul>
            </AlertCard>
          </div>
        )}
        {attention.uncreated.length > 0 && (
          <div data-testid="board-attention-uncreated">
            <AlertCard tone="info" count={attention.uncreated.length}
              title={t('nutrition.foliar.board.attention.uncreated')}>
              <ul className="ntr-fbd-attention__list">
                {attention.uncreated.slice(0, 3).map((u) => (
                  <li key={u.roundId}>
                    {u.name} — {t('nutrition.foliar.board.attention.orchards', { count: u.orchardCount })}
                  </li>
                ))}
              </ul>
            </AlertCard>
          </div>
        )}
        {attention.undated.length > 0 && (
          <div data-testid="board-attention-undated">
            <AlertCard tone="warn" count={attention.undated.length}
              title={t('nutrition.foliar.board.attention.undated')}>
              <ul className="ntr-fbd-attention__list">
                {attention.undated.slice(0, 3).map((u) => (
                  <li key={u.roundId}>{renderRoundLink(u, 'board-attention-undated-round')}</li>
                ))}
              </ul>
              {attention.undated.length > 3 && (
                <p className="ntr-fbd-attention__more">
                  {t('nutrition.foliar.board.attention.undatedMore', { count: attention.undated.length - 3 })}
                </p>
              )}
            </AlertCard>
          </div>
        )}
      </div>

      <DataTable<FoliarBoardRound>
        rows={rounds}
        rowKey={(r) => r.roundId}
        emptyState={t('nutrition.foliar.board.empty')}
        columns={[
          {
            key: 'name',
            label: t('nutrition.foliar.board.col.round'),
            render: (r) => renderRoundLink(r, 'board-round-name'),
          },
          {
            key: 'month', label: t('nutrition.foliar.board.col.month'),
            render: (r) => (
              <span data-testid={`board-month-${r.roundId}`}>
                {formatMonthName(r.month, i18n.language)}
              </span>
            ),
          },
          {
            key: 'products', label: t('nutrition.foliar.board.col.products'),
            render: (r) => (r.productNames.length ? r.productNames.join(', ') : '—'),
          },
          {
            key: 'orchards', label: t('nutrition.foliar.board.col.orchards'), align: 'right',
            render: (r) => r.orchardCount,
          },
          {
            key: 'progress', label: t('nutrition.foliar.board.col.progress'),
            render: (r) => {
              const text = progressOf(r);
              if (!text) return <span className="ntr-muted">—</span>;
              const s = r.sessions;
              const pct = s.total === 0 ? 0
                : Math.round(((s.completed + s.signedOff) / s.total) * 100);
              return (
                <div className="ntr-fbd-progress" data-testid={`board-progress-${r.roundId}`}>
                  <div className="ntr-fbd-progress__track">
                    <div className="ntr-fbd-progress__fill" style={{ width: `${pct}%` }} />
                  </div>
                  <small>{text}</small>
                </div>
              );
            },
          },
          {
            key: 'status', label: t('nutrition.foliar.board.col.status'),
            render: (r) => {
              const state = roundState(r);
              if (state === 'notCreated' && r.orchardCount > 0) {
                return (
                  <button type="button" className="pat-btn pat-btn--subtle"
                    data-testid={`board-create-${r.roundId}`} disabled={busyRound === r.roundId}
                    onClick={() => onCreate(r)}>
                    {t('nutrition.foliar.board.create')}
                  </button>
                );
              }
              return (
                <span data-testid={`board-status-${r.roundId}`}>
                  <StatusPill variant={STATE_VARIANT[state]}>
                    {t(`nutrition.foliar.board.status.${state}`)}
                  </StatusPill>
                </span>
              );
            },
          },
        ]}
        mobileCardRow={(r) => (
          <div>
            <strong>
              {renderRoundLink(r, 'board-card-round-name')} ·{' '}
              <span data-testid={`board-card-month-${r.roundId}`}>
                {formatMonthName(r.month, i18n.language)}
              </span>
            </strong>
            <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>
              {progressOf(r) ?? t(`nutrition.foliar.board.status.${roundState(r)}`)}
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default FoliarBoardPage;
